import { useCallback, useMemo, useState, type FC } from "react";
import { message, Tooltip } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import WizardStepper from "src/components/reusable/WizardStepper/WizardStepper";
import type { CampaignData } from "../../../EngagementInterfaces";
import CampaignPageLayout from "../../shared/CampaignPageLayout";
import CampaignFlowActions from "../../shared/CampaignFlowActions";
import { CAMPAIGN_ANALYTICS_ROUTE } from "../../CampaignSetup/CampaignSetupConstants";
import ScheduleCampaignModal from "../EmailCampaign/AiEmail/components/ScheduleCampaignModal";
import EmailStatusPopup from "../EmailCampaign/AiEmail/EmailStatusPopup";
import ChooseWhatsAppTemplateStep from "./steps/ChooseWhatsAppTemplateStep";
import WhatsAppEditorStep from "./steps/WhatsAppEditorStep";
import WhatsAppTemplateFormScreen from "./steps/WhatsAppTemplateFormScreen";
import { useWhatsAppCampaignWizard } from "./hooks/UseWhatsAppCampaignWizard";
import { useWhatsAppTemplates } from "./hooks/UseWhatsAppTemplates";
import type { WhatsAppTemplate } from "./WhatsAppCampaignTypes";
import {
  NO_LIST_NAMES,
  WHATSAPP_CAMPAIGN_STEPS,
  WHATSAPP_EDITOR_KEY,
} from "./WhatsAppCampaignConstants";
import "../EmailCampaign/AiEmail/AiCampaignWizard.scss";
import "./WhatsAppCampaignEditor.scss";

const hasAiFlag = (state: unknown): boolean =>
  typeof state === "object" && state !== null && "use_ai" in state && state.use_ai === true;

const WIZARD_KEY = "campaignFlow.aiWizard";
const EDITOR_STEP_INDEX = WHATSAPP_CAMPAIGN_STEPS.findIndex(step => step.key === "editor");

interface WhatsAppCampaignWizardProps {
  campaignData: Partial<CampaignData>;
}

const WhatsAppCampaignWizard: FC<WhatsAppCampaignWizardProps> = ({ campaignData }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const templatesState = useWhatsAppTemplates();
  const [campaignName, setCampaignName] = useState(campaignData.campaignName ?? "");
  const [sendTo, setSendTo] = useState(campaignData.sendTo ?? NO_LIST_NAMES);
  const useAi = hasAiFlag(useLocation().state);

  const {
    stepIndex,
    stepKey,
    template,
    drafts,
    media,
    audience,
    preview,
    isComplete,
    isSending,
    isScheduleOpen,
    deliveryStatus,
    selectTemplate,
    updateDraft,
    attachMedia,
    removeMedia,
    goNext,
    goBack,
    changeTemplate,
    sendNow,
    schedule,
    openSchedule,
    closeSchedule,
    clearDeliveryStatus,
  } = useWhatsAppCampaignWizard({
    listNames: sendTo,
    campaignName,
    onTemplatesStale: templatesState.reload,
  });

  const [formTemplate, setFormTemplate] = useState<WhatsAppTemplate | null | undefined>();
  const isFormOpen = formTemplate !== undefined;
  const isEditorStep = stepKey === "editor";

  const showDraftUnavailable = useCallback(
    () => message.info(t("campaignFlow.draft.whatsappUnavailable")),
    [t]
  );
  const openNewTemplateForm = useCallback(() => setFormTemplate(null), []);
  const closeTemplateForm = useCallback(() => setFormTemplate(undefined), []);
  const finishTemplateForm = useCallback(() => {
    setFormTemplate(undefined);
    templatesState.reload();
  }, [templatesState]);

  const stepperSteps = useMemo(
    () => WHATSAPP_CAMPAIGN_STEPS.map(step => ({ key: step.key, title: t(step.titleKey) })),
    [t]
  );

  const canSend = isComplete && campaignName.trim().length > 0 && sendTo.length > 0;
  const isNextDisabled = !template;

  return (
    <CampaignPageLayout
      stickyChrome
      title={t(`${WHATSAPP_EDITOR_KEY}.pageTitle`)}
      onExit={() => navigate(CAMPAIGN_ANALYTICS_ROUTE)}
      stepper={<WizardStepper steps={stepperSteps} currentIndex={isFormOpen ? EDITOR_STEP_INDEX : stepIndex} />}
    >
      {isFormOpen ? (
        <WhatsAppTemplateFormScreen
          template={formTemplate}
          sendTo={sendTo}
          useAi={useAi}
          onBack={closeTemplateForm}
          onFinished={finishTemplateForm}
        />
      ) : isEditorStep && template ? (
        <WhatsAppEditorStep
          template={template}
          drafts={drafts}
          audience={audience}
          media={media}
          preview={preview}
          onDraftChange={updateDraft}
          onAttachMedia={attachMedia}
          onRemoveMedia={removeMedia}
          onChangeTemplate={changeTemplate}
          campaignName={campaignName}
          sendTo={sendTo}
          onCampaignNameChange={setCampaignName}
          onSendToChange={setSendTo}
        />
      ) : (
        <ChooseWhatsAppTemplateStep
          templatesState={templatesState}
          onUse={selectTemplate}
          onEdit={setFormTemplate}
          onCreate={openNewTemplateForm}
        />
      )}

      {!isFormOpen && (
      <CampaignFlowActions sticky onBack={goBack}>
        <button
          type="button"
          className="button button--link campaign-flow__action campaign-flow__action--link"
          onClick={showDraftUnavailable}
        >
          {t("campaignFlow.common.saveDraft")}
        </button>
        {isEditorStep ? (
          <>
            <button
              type="button"
              className="button button--primary campaign-flow__action"
              onClick={openSchedule}
              disabled={!canSend}
            >
              {t(`${WIZARD_KEY}.createCampaign.scheduleCampaign`)}
            </button>
            <button
              type="button"
              className="button button--primary campaign-flow__action"
              onClick={sendNow}
              disabled={!canSend}
            >
              {t(`${WIZARD_KEY}.createCampaign.sendCampaign`)}
            </button>
          </>
        ) : (
          <Tooltip
            title={isNextDisabled ? t(`${WHATSAPP_EDITOR_KEY}.chooseTemplate.nextDisabledHint`) : ""}
            placement="topRight"
            arrow={false}
            overlayClassName="wa-next-tooltip"
          >
            <span className="wa-next-tooltip__trigger">
              <button
                type="button"
                className="button button--primary campaign-flow__action"
                onClick={goNext}
                disabled={isNextDisabled}
              >
                {t(`${WIZARD_KEY}.next`)}
              </button>
            </span>
          </Tooltip>
        )}
      </CampaignFlowActions>
      )}

      {isScheduleOpen && (
        <ScheduleCampaignModal
          isSubmitting={isSending}
          onCancel={closeSchedule}
          onSchedule={schedule}
        />
      )}

      {deliveryStatus && (
        <EmailStatusPopup
          type={deliveryStatus.type}
          date={deliveryStatus.date}
          time={deliveryStatus.time}
          onClose={clearDeliveryStatus}
        />
      )}
    </CampaignPageLayout>
  );
};

export default WhatsAppCampaignWizard;
